import { useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Modal } from 'antd'
import type { AdvicePrepAppliedCoupon } from '../pricing'
import AdvicePrepTrialTerms from './AdvicePrepTrialTerms'

type AdvicePrepPromoModalProps = {
  open: boolean
  validating: boolean
  error: string | null
  /** Set once the parent has validated a code; switches the modal to its terms step. */
  applied: AdvicePrepAppliedCoupon | null
  trialMonths: number | null
  dueToday: number
  /** Already formatted for display, not an ISO instant. */
  trialEndLabel: string | null
  discountSentence: string | null
  onApply: (code: string) => void
  onBack: () => void
  onConfirm: () => void
  onSkip: () => void
  onCancel: () => void
}

/**
 * Asked before the subscription call, so a customer holding a code cannot scroll
 * past the field and get billed in full. The parent owns the applied coupon and
 * runs the validation — this holds only the text being typed, so the card's
 * inline field and this modal can never disagree about what was applied.
 */
export default function AdvicePrepPromoModal({
  open,
  validating,
  error,
  applied,
  trialMonths,
  dueToday,
  trialEndLabel,
  discountSentence,
  onApply,
  onBack,
  onConfirm,
  onSkip,
  onCancel,
}: AdvicePrepPromoModalProps) {
  const [code, setCode] = useState('')

  // A reopened modal must not show the code from last time, which may since have
  // been removed on the card.
  useEffect(() => {
    if (open && !applied) setCode('')
  }, [open, applied])

  const terms = !!applied

  return (
    <Modal
      open={open}
      title={terms ? 'Confirm your free trial' : 'Do you have a promo code?'}
      onCancel={onCancel}
      mask={{ closable: !validating }}
      footer={
        terms
          ? [
              <Button key="back" disabled={validating} onClick={onBack}>
                Back
              </Button>,
              <Button key="confirm" type="primary" loading={validating} onClick={onConfirm}>
                {trialMonths ? `Start ${trialMonths} month free trial` : 'Continue'}
              </Button>,
            ]
          : [
              <Button key="skip" disabled={validating} onClick={onSkip}>
                Continue without a code
              </Button>,
              <Button
                key="apply"
                type="primary"
                loading={validating}
                disabled={!code.trim()}
                onClick={() => onApply(code.trim())}
              >
                Apply
              </Button>,
            ]
      }
    >
      {terms ? (
        <Alert
          type="success"
          showIcon
          message={
            trialMonths
              ? `${applied?.code} applied — ${trialMonths} month free trial`
              : `${applied?.code} applied`
          }
          description={
            <AdvicePrepTrialTerms
              trialMonths={trialMonths}
              dueToday={dueToday}
              trialEndLabel={trialEndLabel}
              discountSentence={discountSentence}
            />
          }
        />
      ) : (
        <Form
          layout="vertical"
          onFinish={() => {
            if (code.trim()) onApply(code.trim())
          }}
        >
          <p className="mb-3 text-sm text-gray-600">
            A promo code is what starts a free trial. Without one, billing begins today.
          </p>
          <Form.Item className="mb-0" validateStatus={error ? 'error' : undefined} help={error ?? undefined}>
            <Input
              id="promoCodeModal"
              autoFocus
              value={code}
              disabled={validating}
              placeholder="Please enter a coupon code if available"
              aria-label="Promo Code"
              onChange={(e) => setCode(e.target.value)}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
